import React, { useState } from 'react'
import { Flex, PageWrapper, PageFooter } from '../../styling/GlobalStyles'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import { BackHeader } from 'components/ui/BackHeader/BackHeader'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { Pagination } from '../../components/Pagination/Pagination'
import { NavBar } from '../../components/NavBar/NavBar'
import { useDispatch, useSelector } from 'react-redux'
import {
  cleanClientMachines,
  deleteClientMachinery,
  getClientMachinery,
} from 'features/clientMachinery/clientMachinerySlice'
import { useNavigate, useParams } from 'react-router-dom'
import { AppDispatch, RootState } from 'store'
import { NoItems } from 'components/NoItems-fallback/NoItems'
import { getParams, handleMachineStatus, handlePagination } from 'utils/helpers'
import { setConfirmModal } from 'features/global/globalSlice'
import { ModalTypes } from 'types/global.interface'
import { clearSingleMachineryCategory } from 'features/categories/machineryCategories'
import useQueryPagination from 'hooks/useQueryPagination'
import { getMachineCategoryIcon } from 'pages/Machinery/machineCategoryIcons'
import { tokens } from 'styling/tokens'
import { IStatusPill } from 'types/ui/statusPill.interface'

const machineStatusTone: Record<number, IStatusPill['status']> = {
  1: 'success',
  2: 'accent',
  3: 'danger',
  4: 'warning',
  5: 'neutral',
}

export const SingleMachineryCategory = () => {
  const dispatch = useDispatch<AppDispatch>()
  const { id } = useParams()
  const { totalPages, clientMachines } = useSelector(
    (store: RootState) => store.clientMachinery,
  )
  const navigate = useNavigate()
  const [params, setParams] = useState<any>(getParams({ location, limit: 30 }))

  useQueryPagination({
    fetchFunctions: [getClientMachinery],
    cleanUpFunctions: [cleanClientMachines, clearSingleMachineryCategory],
    params,
    functionParams: { ...params, machineCategoryId: id },
  })

  const handleDelete = (machineId: number) => {
    dispatch(
      setConfirmModal({
        isOpen: true,
        onSave: async () => {
          await dispatch(deleteClientMachinery(machineId))
        },
        setParams: {
          type: ModalTypes.Delete,
          f: setParams,
          noItems: clientMachines.length < 2,
        },
        word: 'delete machinery',
      }),
    )
  }

  const columns: IHonestTableColumn<any>[] = [
    { id: 'name', header: 'Name', cell: (m) => m.name },
    {
      id: 'category',
      header: 'Category',
      cell: (m) => (
        <Flex center gap='0.5rem'>
          {getMachineCategoryIcon(m.machineCategory?.iconName, tokens.accent.base, 16)}
          <span>{m.machineCategory?.name ?? '-'}</span>
        </Flex>
      ),
    },
    { id: 'model', header: 'Model', cell: (m) => m.machineryModel?.name ?? '-' },
    {
      id: 'status',
      header: 'Status',
      cell: (m) => (
        <StatusPill status={machineStatusTone[m.status] ?? 'neutral'}>
          {handleMachineStatus(m.status)}
        </StatusPill>
      ),
    },
  ]

  return (
    <div>
      <PageWrapper>
        <NavBar input placeholder='Search Machines...' setParams={setParams} limit={30} />
        <BackHeader title={params?.name} onBack={() => navigate(-1)} />
        {!clientMachines.length ? (
          <NoItems title='machinery' />
        ) : (
          <HonestTable
            columns={columns}
            data={clientMachines}
            keyField='id'
            onClick={(m) => navigate(`/dashboard/machines/${m.id.toString()}`)}
            menuOptions={[
              {
                label: 'Delete',
                color: tokens.status.danger.fg,
                onClick: (m) => handleDelete(m.id),
              },
            ]}
          />
        )}
        <PageFooter>
          <Pagination
            setPage={(page) => handlePagination(page, setParams, 30)}
            pageCount={Math.ceil(totalPages)}
            forcePage={(params.page ? params.page : 1) - 1}
          />
        </PageFooter>
      </PageWrapper>
    </div>
  )
}
